import { useCallback } from 'react';
import { AutocompleteClasses } from '@mui/material';

import { useTranslation } from '@lyearn/i18n';
import { InfoOutline } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Box, Stack, Tooltip, Typography } from '@lyearn/ui';
import UserFilter from '@/components/Form/components/Conditions/components/FilterValues/UserFilter';
import { ReviewerConfigInput, ReviewerTypeEnum } from '@/types/schema';

import { validateDefaultReviewerError, validateSpecificReviewerError } from '../utils';

function SelectUser({
  reviewerConfig,
  showError,
  type,
  onReviewerConfigChange,
  className,
  classes,
}: {
  reviewerConfig: ReviewerConfigInput;
  showError?: boolean;
  type: ReviewerTypeEnum;
  onReviewerConfigChange: (nextValue: ReviewerConfigInput) => void;
  className?: string;
  classes?: Partial<AutocompleteClasses>;
}) {
  const { t } = useTranslation('Group');
  const { defaultReviewerId } = reviewerConfig;

  const onDefaultReviewerChange = useCallback(
    (value?: string) => {
      onReviewerConfigChange({ ...reviewerConfig, defaultReviewerId: value });
    },
    [onReviewerConfigChange, reviewerConfig],
  );

  const error =
    type === ReviewerTypeEnum.Specific
      ? validateSpecificReviewerError(reviewerConfig)
      : validateDefaultReviewerError(reviewerConfig);

  return (
    <Box className={cn('space-y-8', className)}>
      {type === ReviewerTypeEnum.Specific ? (
        <Typography className="ms-8" color="text-text-primary" variant="body-short-02">
          {t('Exercise:Creator.DefaultReviewer')}
        </Typography>
      ) : (
        <Stack alignItems="center" className="ms-8">
          <Typography color="text-text-primary" variant="body-short-02">
            {t('Exercise:Creator.DefaultReviewer')}
          </Typography>
          <Tooltip
            placement="right"
            title={
              type === ReviewerTypeEnum.Dynamic
                ? t('Exercise:Creator.DefaultReviewerTooltipInfo')!
                : t('Exercise:Creator.GroupDefaultReviewerTooltipInfo')!
            }>
            <Box>
              <InfoOutline className="h-16 w-16 ms-4" fillColorToken="icon-secondary" />
            </Box>
          </Tooltip>
        </Stack>
      )}
      <UserFilter
        key={type}
        crossSubWorkspace
        showEmail
        className={''}
        classes={classes}
        clearOnBlur={false}
        error={Boolean(showError && error)}
        filterValue={defaultReviewerId}
        placeholder={t('Exercise:Creator.SelectReviewer')}
        size="large"
        onFilterValueChange={onDefaultReviewerChange}
      />
      {showError && error ? (
        <Typography className="ms-8" color="text-text-error" variant="body-short-03">
          {error}
        </Typography>
      ) : null}
    </Box>
  );
}

export default SelectUser;
